import React from 'react';
import {
  APPROVAL_REASON_LABELS,
  APPROVAL_STATUS_LABELS,
  getApprovalActionLabel,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { AdminApprovalRequestDetailView } from '../../../services/admin-approval-requests/admin-approval-requests.service';
import classes from '../monitoring-page.module.scss';

type Props = {
  detail: AdminApprovalRequestDetailView;
};

type DetailLine = {
  key: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discountInput?: number;
};

const formatMoney = (amount: number) => `₪${Number(amount).toFixed(2)}`;

const getOrderNumber = (detail: AdminApprovalRequestDetailView) => {
  const orderCode = detail.orderCode?.trim();
  if (!orderCode) return String(detail.orderId);

  const parts = orderCode.split('/').map((part) => part.trim()).filter(Boolean);
  return parts.length >= 2 ? parts.slice(-2).join('/') : orderCode;
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={classes.detailField}>
    <span className={classes.detailLabel}>{label}</span>
    <span className={classes.detailValue}>{value || '-'}</span>
  </div>
);

const getReasonLabel = (detail: AdminApprovalRequestDetailView) => {
  const reasonLabel = APPROVAL_REASON_LABELS[detail.reason];
  if (detail.reason !== 'other' || !detail.note?.trim()) {
    return reasonLabel;
  }
  return `${reasonLabel} - ${detail.note.trim()}`;
};

const getLines = (detail: AdminApprovalRequestDetailView): DetailLine[] => {
  const lineById = new Map(detail.orderlines.map((line) => [line.orderlineId, line]));

  switch (detail.payload.actionType) {
    case 'item_discount':
    case 'on_the_house':
      return detail.payload.items.map((item) => {
        const line = lineById.get(item.orderlineId);
        return {
          key: `${item.orderlineId}`,
          productName: line?.productName ?? 'פריט כללי',
          quantity: item.quantity,
          unitPrice: line?.unitPrice ?? 0,
          discountInput: item.discountInput,
        };
      });
    case 'cancel':
      return detail.payload.items.map((item) => {
        const line = lineById.get(item.orderlineId);
        return {
          key: `${item.orderlineId}`,
          productName: line?.productName ?? 'פריט כללי',
          quantity: item.quantity,
          unitPrice: line?.unitPrice ?? 0,
        };
      });
    default:
      return [];
  }
};

const ActionDetails: React.FC<Props> = ({ detail }) => {
  if (detail.payload.actionType === 'global_discount') {
    return (
      <div className={classes.detailsSection}>
        <h3 className={classes.sectionTitle}>{'פרטי ההנחה'}</h3>
        {detail.payload.discountMode === 'amount' ? (
          <>
            <DetailField label="סכום ההנחה" value={formatMoney(detail.payload.discountInput)} />
            <DetailField label="סכום הזמנה נוכחי לאחר הנחה" value={formatMoney(detail.currentOrderAmount)} />
          </>
        ) : (
          <p className={classes.detailNote}>
            {`הנחה של ${detail.payload.discountInput}% על כל ההזמנה, כולל פריטים שיתווספו בהמשך.`}
          </p>
        )}
      </div>
    );
  }

  if (detail.payload.actionType === 'general_item') {
    return (
      <div className={classes.detailsSection}>
        <h3 className={classes.sectionTitle}>{'פרטי פריט כללי'}</h3>
        <DetailField label="מחיר יחידה" value={formatMoney(detail.payload.unitPrice)} />
        <DetailField label="כמות" value={`×${detail.payload.quantity}`} />
        <DetailField label="סה״כ" value={formatMoney(detail.payload.unitPrice * detail.payload.quantity)} />
      </div>
    );
  }

  const lines = getLines(detail);
  const showDiscount = detail.payload.actionType === 'item_discount' || detail.payload.actionType === 'on_the_house';

  return (
    <div className={classes.detailsSection}>
      <h3 className={classes.sectionTitle}>{'שורות הזמנה'}</h3>
      {lines.length === 0 ? (
        <div className={classes.empty}>{'אין שורות להצגה.'}</div>
      ) : (
        <table className={classes.itemsTable}>
          <thead>
            <tr>
              <th>{'פריט'}</th>
              <th>{'כמות'}</th>
              <th>{'סכום מקורי'}</th>
              {showDiscount && <th>{'הנחה'}</th>}
              {showDiscount && <th>{'סכום לאחר הנחה'}</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const originalAmount = line.unitPrice * line.quantity;
              const discount = line.discountInput ?? 0;
              return (
                <tr key={line.key}>
                  <td>{line.productName}</td>
                  <td>{`×${line.quantity}`}</td>
                  <td>{formatMoney(originalAmount)}</td>
                  {showDiscount && <td>{formatMoney(discount)}</td>}
                  {showDiscount && <td>{formatMoney(Math.max(0, originalAmount - discount))}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const ApprovalRequestDetail: React.FC<Props> = ({ detail }) => (
  <div className={classes.detailRoot}>
    <div className={classes.detailsGrid}>
      <div className={classes.detailsSection}>
        <h3 className={classes.sectionTitle}>{'פרטי בקשה'}</h3>
        <DetailField label="מספר הזמנה" value={getOrderNumber(detail)} />
        <DetailField label="שולחן" value={detail.tableLabel} />
        <DetailField label="סוג פעולה" value={getApprovalActionLabel(detail)} />
        <DetailField label="סיבה" value={getReasonLabel(detail)} />
      </div>

      <div className={classes.detailsSection}>
        <h3 className={classes.sectionTitle}>{'סטטוס ואנשים'}</h3>
        <DetailField label="סטטוס" value={APPROVAL_STATUS_LABELS[detail.status]} />
        <DetailField label="נשלח ע״י" value={detail.requestedByName} />
        <DetailField
          label={detail.status === 'approved' ? 'אושר ע״י' : detail.status === 'denied' ? 'נדחה ע״י' : 'אושר/נדחה ע״י'}
          value={detail.decidedByName}
        />
        <DetailField label="נוצר ב" value={formatDateTime(detail.createdAt)} />
      </div>
    </div>

    <ActionDetails detail={detail} />
  </div>
);
